import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLoginMutation } from '../api/JsdhNotifikaceService/service/AuthMutation';
import { useJednotkaQuery } from '../api/JsdhNotifikaceService/service/AdminMutations';
import AppearanceMenu from '../components/AppearanceMenu';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isAdmin } = useAuth();
    const loginMutation = useLoginMutation();
    const { data: jednotka } = useJednotkaQuery();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Safely extract the 'from' pathname from location state
    const getFromPath = (): string => {
        const state = location.state;
        if (state && typeof state === 'object' && 'from' in state) {
            const from = (state as { from?: { pathname?: string } }).from;
            if (from && typeof from.pathname === 'string') {
                return from.pathname;
            }
        }
        return '/';
    };
    const from = getFromPath();

    useEffect(() => {
        if (isAuthenticated) {
            const target = (from === '/' && isAdmin) ? '/admin' : from;
            navigate(target, { replace: true });
        }
    }, [isAuthenticated, isAdmin, navigate, from]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email.trim() || !password.trim()) {
            return;
        }

        loginMutation.mutate(
            { email, password },
            {
                onSuccess: () => {
                    // useEffect handles redirect based on isAuthenticated + isAdmin
                },
            }
        );
    };

    const handleTogglePassword = () => {
        setShowPassword((prev) => !prev);
    };

    return (
        <Container maxWidth="xs" sx={{ mt: 8 }} data-testid="prihlaseni">
            {/* Theme toggle in top-right corner */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    zIndex: 1000,
                }}
            >
                <AppearanceMenu />
            </Box>

            <Paper elevation={3} sx={{ p: 4 }}>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    mb={3}
                >
                    <Typography variant="h4" component="h1" gutterBottom>
                        Přihlášení
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {jednotka?.nazev || 'Hasiči Online'} - Administrace
                    </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        variant="outlined"
                        margin="normal"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loginMutation.isPending}
                        autoComplete="email"
                        autoFocus
                    />

                    <TextField
                        fullWidth
                        label="Heslo"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        margin="normal"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loginMutation.isPending}
                        autoComplete="current-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleTogglePassword}
                                        edge="end"
                                        disabled={loginMutation.isPending}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={loginMutation.isPending || !email.trim() || !password.trim()}
                        sx={{ mt: 3, mb: 2, height: 48 }}
                    >
                        {loginMutation.isPending ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Přihlásit se'
                        )}
                    </Button>

                    <Box textAlign="center" mt={1}>
                        <Typography
                            variant="body2"
                            component={Link}
                            to="/zapomenute-heslo"
                            sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            Zapomenuté heslo?
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default LoginPage;
